/**
 * Firestore Helpers
 * Robust converters for Firestore Timestamp to Date conversions
 * and document normalization functions with runtime validation
 */

import type {
  UserDocument,
  CheckinDocument,
  HouseholdDocument,
  HouseholdMemberDocument,
  HouseholdMemberLastShared,
  NudgeDocument,
  DocumentRecord,
  DocumentType,
  DocumentStatus,
  ConfidenceLevel,
  ProcessingState,
  TrackingEvent,
  TrackingEventType,
  FeedingType,
  DiaperType,
  NoteCategory,
} from './types';

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate a value is one of allowed options
 */
function validateEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T
): T {
  if (typeof value === 'string' && (allowed as readonly string[]).includes(value)) {
    return value as T;
  }
  return fallback;
}

/**
 * Validate a string field with max length
 */
function validateString(value: unknown, maxLength: number = 500, fallback: string = ''): string {
  if (typeof value !== 'string') return fallback;
  return value.slice(0, maxLength);
}

/**
 * Validate an optional string field
 */
function validateOptionalString(value: unknown, maxLength: number = 500): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.slice(0, maxLength) : undefined;
}

/**
 * Validate a number field with bounds
 */
function validateNumber(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || isNaN(value)) return fallback;
  return Math.max(min, Math.min(max, value));
}

/**
 * Validate an optional number field
 */
function validateOptionalNumber(value: unknown, min?: number, max?: number): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== 'number' || isNaN(value)) return undefined;
  let result = value;
  if (min !== undefined) result = Math.max(min, result);
  if (max !== undefined) result = Math.min(max, result);
  return result;
}

/**
 * Validate a boolean field
 */
function validateBoolean(value: unknown, fallback: boolean = false): boolean {
  if (typeof value === 'boolean') return value;
  return fallback;
}

/**
 * Validate an array field
 * Note: Kept for potential future use or external callers
 */
function _validateArray<T>(value: unknown, validator: (item: unknown) => T | undefined): T[] {
  if (!Array.isArray(value)) return [];
  return value.map(validator).filter((item): item is T => item !== undefined);
}

/**
 * Validate string array
 */
function validateStringArray(value: unknown, maxItems: number = 50): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .slice(0, maxItems);
}

/**
 * Type guard for Firestore Timestamp-like objects
 */
function isTimestamp(value: unknown): value is { toDate: () => Date } {
  return (
    value !== null &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate: unknown }).toDate === 'function'
  );
}

/**
 * Convert any date-like value to a JavaScript Date
 * Handles: Date, Firestore Timestamp, ISO string, milliseconds number, null/undefined
 */
export function toDate(value: unknown): Date | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  // Already a Date
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value;
  }

  // Firestore Timestamp with toDate method
  if (isTimestamp(value)) {
    try {
      return value.toDate();
    } catch {
      return undefined;
    }
  }

  // ISO string
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  // Milliseconds number
  if (typeof value === 'number') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  return undefined;
}

/**
 * Convert to Date with a required fallback (never undefined)
 */
export function toDateRequired(value: unknown, fallback: Date = new Date()): Date {
  return toDate(value) ?? fallback;
}

/**
 * Normalize a user document from Firestore
 */
export function normalizeUserDoc(data: Record<string, unknown>): UserDocument {
  return {
    displayName: (data.displayName as string) || 'Friend',
    createdAt: toDateRequired(data.createdAt),
    timezone: (data.timezone as string) || Intl.DateTimeFormat().resolvedOptions().timeZone,
    defaultTimeOption: (data.defaultTimeOption as 1 | 3 | 5) || 3,
    focusAreas: (data.focusAreas as UserDocument['focusAreas']) || [],
    mode: (data.mode as UserDocument['mode']) || 'normal',
    modeUntil: toDate(data.modeUntil),
    notificationPrefs: (data.notificationPrefs as UserDocument['notificationPrefs']) || { enabled: false },
    lastCheckinDate: data.lastCheckinDate as string | undefined,
    momentum7d: (data.momentum7d as number) || 0,
    totalCredits: (data.totalCredits as number) || 0,
    completedCount: (data.completedCount as number) || 0,
    partnerHouseholdId: data.partnerHouseholdId as string | undefined,
    sharingLevel: data.sharingLevel as UserDocument['sharingLevel'],
    onboardingCompleted: data.onboardingCompleted as boolean | undefined,
    // Baby profile fields from onboarding
    babyName: data.babyName as string | undefined,
    babyBirthday: toDate(data.babyBirthday),
    babyGender: data.babyGender as UserDocument['babyGender'],
    babyAge: data.babyAge as string | undefined,
    caregiverRole: data.caregiverRole as UserDocument['caregiverRole'],
    feedingPreference: data.feedingPreference as UserDocument['feedingPreference'],
    feedingPreferences: data.feedingPreferences as UserDocument['feedingPreferences'],
    sleepSchedule: data.sleepSchedule as UserDocument['sleepSchedule'],
    wellnessPreferences: data.wellnessPreferences as string | undefined,
    photoShareToken: data.photoShareToken as string | undefined,
    defaultChatSessionId: data.defaultChatSessionId as string | undefined,
    pinnedChatSessionIds: (data.pinnedChatSessionIds as string[] | undefined)?.slice(0, 5),
    volumeUnit: data.volumeUnit as UserDocument['volumeUnit'],
  };
}

/**
 * Normalize a checkin document from Firestore
 */
export function normalizeCheckin(id: string, data: Record<string, unknown>): CheckinDocument {
  return {
    id,
    date: data.date as string,
    mood: data.mood as number,
    energy: data.energy as number | undefined,
    stress: data.stress as number | undefined,
    noteSaved: (data.noteSaved as boolean) || false,
    noteText: data.noteText as string | undefined,
    computedState: (data.computedState as CheckinDocument['computedState']) || 'steady',
    createdAt: toDateRequired(data.createdAt),
  };
}

/**
 * Normalize a household document from Firestore
 */
export function normalizeHousehold(id: string, data: Record<string, unknown>): HouseholdDocument {
  return {
    id,
    memberUids: (data.memberUids as string[]) || [],
    createdAt: toDateRequired(data.createdAt),
    sharedGoal: data.sharedGoal as HouseholdDocument['sharedGoal'],
  };
}

/**
 * Normalize a household member document from Firestore
 */
export function normalizeHouseholdMember(uid: string, data: Record<string, unknown>): HouseholdMemberDocument {
  const lastShared = data.lastShared as Record<string, unknown> | undefined;
  const todayShared = data.todayShared as Record<string, unknown> | undefined;

  return {
    uid,
    displayName: (data.displayName as string) || '',
    sharingLevel: (data.sharingLevel as HouseholdMemberDocument['sharingLevel']) || 'status',
    lastShared: lastShared ? {
      date: lastShared.date as string | undefined,
      state: lastShared.state as HouseholdMemberLastShared['state'],
      mood: lastShared.mood as number | undefined,
      energy: lastShared.energy as number | undefined,
      stress: lastShared.stress as number | undefined,
      updatedAt: toDate(lastShared.updatedAt),
    } : undefined,
    todayShared: todayShared ? {
      date: todayShared.date as string,
      credits: todayShared.credits as number | undefined,
      completionsCount: todayShared.completionsCount as number | undefined,
    } : undefined,
    updatedAt: toDateRequired(data.updatedAt),
  };
}

/**
 * Normalize a nudge document from Firestore
 */
export function normalizeNudge(id: string, data: Record<string, unknown>): NudgeDocument {
  return {
    id,
    fromUid: data.fromUid as string,
    toUid: data.toUid as string,
    templateId: data.templateId as NudgeDocument['templateId'],
    templateText: data.templateText as string,
    customText: data.customText as string | undefined,
    status: (data.status as NudgeDocument['status']) || 'sent',
    createdAt: toDateRequired(data.createdAt),
    viewedAt: toDate(data.viewedAt),
  };
}

/**
 * Normalize a calendar event document from Firestore
 */
export function normalizeCalendarEvent(id: string, data: Record<string, unknown>): import('./types').CalendarEventDocument {
  return {
    id,
    title: (data.title as string) || '',
    date: (data.date as string) || '',
    time: data.time as string | undefined,
    endTime: data.endTime as string | undefined,
    category: (data.category as import('./types').CalendarEventCategory) || 'other',
    notes: data.notes as string | undefined,
    location: data.location as string | undefined,
    link: data.link as string | undefined,
    reminder: data.reminder as import('./types').ReminderOffset | undefined,
    isAllDay: (data.isAllDay as boolean) ?? true,
    createdByUid: (data.createdByUid as string) || '',
    createdByName: (data.createdByName as string) || '',
    createdAt: toDateRequired(data.createdAt),
    updatedAt: toDateRequired(data.updatedAt),
  };
}

// ============================================================================
// DOCUMENT RECORD VALIDATION
// ============================================================================

/** Valid document types */
const VALID_DOC_TYPES: readonly DocumentType[] = [
  'doctor_visit_summary', 'test_results', 'imaging_report', 'discharge_summary',
  'immunization_record', 'referral_letter', 'prescription', 'growth_chart',
  'insurance_card', 'consent_form', 'bill_statement', 'other',
];

/** Valid document statuses */
const VALID_DOC_STATUSES: readonly DocumentStatus[] = ['uploading', 'processing', 'ready', 'failed'];

/** Valid confidence levels */
const VALID_CONFIDENCE_LEVELS: readonly ConfidenceLevel[] = ['high', 'medium', 'low'];

/** Valid processing states */
const VALID_PROCESSING_STATES: readonly ProcessingState[] = [
  'uploading', 'extracting_text', 'analyzing', 'embedding', 'complete', 'failed',
];

/**
 * Normalize a document record from Firestore with validation
 * Always returns a valid DocumentRecord or null if completely invalid
 * Uses documentDate with createdAt fallback for display consistency
 */
export function normalizeDocumentRecord(id: string, data: Record<string, unknown>): DocumentRecord | null {
  // Required fields - if missing, document is invalid
  if (!id || typeof data !== 'object' || data === null) {
    return null;
  }

  // Get dates with fallbacks
  const createdAt = toDateRequired(data.createdAt);
  const updatedAt = toDateRequired(data.updatedAt, createdAt);
  const documentDate = toDate(data.documentDate);
  const processedAt = toDate(data.processedAt);

  // Parse processing status if present
  let processing: DocumentRecord['processing'] | undefined;
  if (data.processing && typeof data.processing === 'object') {
    const proc = data.processing as Record<string, unknown>;
    processing = {
      state: validateEnum(proc.state, VALID_PROCESSING_STATES, 'uploading'),
      progress: validateNumber(proc.progress, 0, 100, 0),
      lastError: validateOptionalString(proc.lastError, 500),
      updatedAt: toDateRequired(proc.updatedAt),
    };
  }

  // Parse summary if present
  let summary: DocumentRecord['summary'] | undefined;
  if (data.summary && typeof data.summary === 'object') {
    const sum = data.summary as Record<string, unknown>;
    summary = {
      shortSummary: validateString(sum.shortSummary, 300, ''),
      longSummary: validateOptionalString(sum.longSummary, 2000),
      parentFriendly: validateString(sum.parentFriendly, 500, ''),
    };
    // Only include summary if it has content
    if (!summary.shortSummary && !summary.parentFriendly) {
      summary = undefined;
    }
  }

  return {
    id,
    userId: validateString(data.userId, 128, ''),
    babyId: validateString(data.babyId, 128, 'default'),
    householdId: validateOptionalString(data.householdId, 128),
    source: validateEnum(data.source, ['upload', 'photo', 'pdf'] as const, 'upload'),
    mimeType: validateString(data.mimeType, 100, 'application/octet-stream'),
    pageCount: validateNumber(data.pageCount, 0, 1000, 1),
    fileSize: validateNumber(data.fileSize, 0, Number.MAX_SAFE_INTEGER, 0),
    sha256: validateOptionalString(data.sha256, 64),
    originalPath: validateString(data.originalPath, 500, ''),
    thumbnailPath: validateOptionalString(data.thumbnailPath, 500),
    pageImagePaths: validateStringArray(data.pageImagePaths, 100),
    docType: validateEnum(data.docType, VALID_DOC_TYPES, 'other'),
    docTypeConfidence: validateEnum(data.docTypeConfidence, VALID_CONFIDENCE_LEVELS, 'low'),
    documentDate,
    dateConfidence: data.documentDate ? validateEnum(data.dateConfidence, VALID_CONFIDENCE_LEVELS, 'low') : undefined,
    dateSource: validateOptionalString(data.dateSource, 20) as DocumentRecord['dateSource'],
    providerName: validateOptionalString(data.providerName, 200),
    facilityName: validateOptionalString(data.facilityName, 200),
    pages: [], // Pages are loaded separately
    summary,
    title: validateOptionalString(data.title, 200),
    tags: validateStringArray(data.tags, 20),
    notes: validateOptionalString(data.notes, 1000),
    visibleInChat: validateBoolean(data.visibleInChat, true),
    status: validateEnum(data.status, VALID_DOC_STATUSES, 'processing'),
    errorMessage: validateOptionalString(data.errorMessage, 500),
    processing,
    createdAt,
    updatedAt,
    processedAt,
    uploadedByUid: validateString(data.uploadedByUid, 128, ''),
    uploadedByName: validateOptionalString(data.uploadedByName, 100),
  };
}

/**
 * Get display date for a document
 * Returns documentDate if valid, otherwise falls back to createdAt
 */
export function getDocumentDisplayDate(doc: DocumentRecord): Date {
  return doc.documentDate ?? doc.createdAt;
}

// ============================================================================
// TRACKING EVENT VALIDATION
// ============================================================================

/** Valid tracking event types */
const VALID_TRACKING_TYPES: readonly TrackingEventType[] = ['feeding', 'pumping', 'diaper', 'note'];

/** Valid feeding types */
const VALID_FEEDING_TYPES: readonly FeedingType[] = ['breast_left', 'breast_right', 'breast_both', 'bottle', 'solids'];

/** Valid diaper types */
const VALID_DIAPER_TYPES: readonly DiaperType[] = ['wet', 'dirty', 'both'];

/** Valid note categories */
const VALID_NOTE_CATEGORIES: readonly NoteCategory[] = ['spit_up', 'fussy', 'milestone', 'medicine', 'other'];

/**
 * Normalize a tracking event from Firestore with validation
 * Returns null if the event is completely invalid
 */
export function normalizeTrackingEvent(id: string, data: Record<string, unknown>): TrackingEvent | null {
  if (!id || typeof data !== 'object' || data === null) {
    return null;
  }

  const type = validateEnum(data.type, VALID_TRACKING_TYPES, 'note');
  const createdAt = toDateRequired(data.createdAt);
  const userId = validateString(data.userId, 128, '');
  const notes = validateOptionalString(data.notes, 500);

  const base = { id, userId, createdAt, notes };

  switch (type) {
    case 'feeding':
      return {
        ...base,
        type: 'feeding',
        feedingType: validateEnum(data.feedingType, VALID_FEEDING_TYPES, 'bottle'),
        durationMinutes: validateOptionalNumber(data.durationMinutes, 0, 1440),
        amountMl: validateOptionalNumber(data.amountMl, 0, 2000),
      };
    case 'pumping':
      return {
        ...base,
        type: 'pumping',
        side: validateEnum(data.side, ['left', 'right', 'both'] as const, 'both'),
        durationMinutes: validateNumber(data.durationMinutes, 0, 1440, 0),
        amountMl: validateOptionalNumber(data.amountMl, 0, 2000),
      };
    case 'diaper':
      return {
        ...base,
        type: 'diaper',
        diaperType: validateEnum(data.diaperType, VALID_DIAPER_TYPES, 'wet'),
      };
    case 'note':
    default:
      return {
        ...base,
        type: 'note',
        category: validateEnum(data.category, VALID_NOTE_CATEGORIES, 'other'),
        text: validateOptionalString(data.text, 500),
      };
  }
}
